/**
 * useCountryMap · 国家元信息映射 Hook
 *
 * 通过 cond 接口（materials_config_region_countries）拉国家字典。
 * cond 数据是二级结构（region → countries[]），本 hook 输出两份：
 * - `regionOptions`：原 cond 二级结构（用于 RegionSelect 的 options）
 * - `countries` / `countryMap`：扁平化的 country 列表（用于业务表格 / 反查名称）
 *
 * 完全对齐 ADCountryRegion `condDealwith` 的转换逻辑（参考
 * `src/pages/marketing/components/FilterCommon/config/CompetitorsAdCreatives.tsx`）
 */
import { useMemo } from 'react';
import { useFilterCondData } from '@opm/components/FilterCommon/hooks/useFilterCondData';
import { countryEn, countryCn } from '@op/constants';

interface ICondCountryRaw {
  country_code: string;
  country_en: string;
  country_zh: string;
}

interface ICondRegionRaw {
  region_code: string;
  region_en: string;
  region_zh: string;
  countries: ICondCountryRaw[];
}

/** 二级结构选项（与 RegionSelect 期望的 TSecondOptionsItem 一致） */
export interface IRegionOption {
  value: string;
  text: string;
  textCN: string;
  children: Array<{
    value: string;
    text: string;
    textCN: string;
    /** 原始英文 country_en，用于 getCountryFlag 查找 */
    code: string;
  }>;
}

/** 国家元信息（扁平结构，业务表格使用） */
export interface ICountryMeta {
  country_code: string;
  country_name: string;
  country_name_cn: string;
  /** 原始英文 country_en，用于 flag 查找 */
  code: string;
}

export type TCountryMap = Record<string, ICountryMeta>;

export interface IUseCountryMapReturn {
  /** RegionSelect 用的二级 options */
  regionOptions: IRegionOption[];
  /** 扁平国家列表（按 cond 顺序） */
  countries: ICountryMeta[];
  /** 国家字典（key 为小写 country_code） */
  countryMap: TCountryMap;
  loading: boolean;
}

/** 兼容 cond 数据的两种返回结构 */
function pickRegions(data: any): ICondRegionRaw[] {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return data.region_country || data.countries || [];
}

export function useCountryMap(): IUseCountryMapReturn {
  const { condData, loading } = useFilterCondData({
    keys: [],
    extrRequestKeys: ['materials_config_region_countries'],
  });

  /** RegionSelect 二级 options（与 ADCountryRegion condDealwith 一致） */
  const regionOptions = useMemo<IRegionOption[]>(() => {
    const regions = pickRegions(condData?.materials_config_region_countries);
    return regions.map((region) => ({
      value: region.region_code?.toLocaleLowerCase?.(),
      text: region.region_en,
      textCN: region.region_zh,
      children: (region.countries || []).map((c) => {
        const enKey = c.country_en?.toLocaleLowerCase?.();
        return {
          value: c.country_code?.toLocaleLowerCase?.(),
          text: countryEn[enKey] || c.country_en,
          textCN: countryCn[enKey] || c.country_zh,
          code: c.country_en,
        };
      }),
    }));
  }, [condData]);

  /** 扁平国家列表（业务表格用） */
  const countries = useMemo<ICountryMeta[]>(() => {
    const result: ICountryMeta[] = [];
    regionOptions.forEach((region) => {
      region.children.forEach((c) => {
        if (!c.value) return;
        result.push({
          country_code: c.value,
          country_name: c.text,
          country_name_cn: c.textCN,
          code: c.code,
        });
      });
    });
    return result;
  }, [regionOptions]);

  const countryMap = useMemo<TCountryMap>(() => {
    const m: TCountryMap = {};
    countries.forEach((c) => {
      m[c.country_code] = c;
    });
    return m;
  }, [countries]);

  return { regionOptions, countries, countryMap, loading };
}
